import { lazy } from "react";
import type { SkinDef } from "../../types";
import { Login } from "./Login";
import { DEFAULT_TOKENS } from "./tokens";

/**
 * Heavy presentation slots — lazy so the cold login load no longer
 * barrels the game + lobby chrome (react-window / Table / Seat /
 * Board / HUD / ActionBar / SpectatorPanel / modals) into the entry
 * chunk. Each slot is a NAMED export, so the `{ default }` contract
 * `React.lazy` expects is adapted in the thunk. A lazy value is a
 * `ComponentType<P>` that satisfies `SkinSlots[K]` and suspends to
 * the nearest Suspense ancestor; every render site (the Game + Lobby
 * route arms in `AuthGateContent`) is already wrapped in `<Suspense>`,
 * so the slot lookup in `useSkinSlot` is unchanged.
 *
 * `Login` stays EAGER below — it is the first-paint critical surface
 * rendered in the `logged_out` arm OUTSIDE any Suspense boundary, so
 * deferring its chunk would both throw (no Suspense ancestor) and add
 * a round-trip to the cold path it already blocks on.
 */
const Table = lazy(() =>
  import("./table/Table").then((m) => ({ default: m.Table })),
);
const Card = lazy(() =>
  import("./table/Card").then((m) => ({ default: m.Card })),
);
const Seat = lazy(() =>
  import("./table/Seat").then((m) => ({ default: m.Seat })),
);
const Board = lazy(() =>
  import("./table/Board").then((m) => ({ default: m.Board })),
);
const HUD = lazy(() =>
  import("./HUD/HUDShell").then((m) => ({ default: m.HUD })),
);
const ActionBar = lazy(() =>
  import("./ActionBar/ActionBar").then((m) => ({ default: m.ActionBar })),
);
const SpectatorPanel = lazy(() =>
  import("./SpectatorPanel/Panel").then((m) => ({ default: m.SpectatorPanel })),
);
const SettledSummary = lazy(() =>
  import("./SettledSummary/Summary").then((m) => ({
    default: m.SettledSummary,
  })),
);
const Showdown = lazy(() =>
  import("./modals/Showdown").then((m) => ({ default: m.Showdown })),
);
const RebuyModal = lazy(() =>
  import("./modals/RebuyModal").then((m) => ({ default: m.RebuyModal })),
);
const Lobby = lazy(() =>
  import("./Lobby/Lobby").then((m) => ({ default: m.Lobby })),
);

/**
 * The default skin (Korean Texas Hold'em PokerStars-leaning
 * idiom). Re-exported as the canonical fallback for `ThemeProvider`
 * + every component slot lookup.
 */
export const DEFAULT_SKIN: SkinDef = {
  name: "default",
  cssVars: DEFAULT_TOKENS,
  components: {
    Table,
    Card,
    Seat,
    Board,
    HUD,
    ActionBar,
    SettledSummary,
    SpectatorPanel,
    Showdown,
    Lobby,
    RebuyModal,
    Login,
  },
};

export {
  ActionBar,
  Board,
  Card,
  HUD,
  Lobby,
  Login,
  RebuyModal,
  Seat,
  SettledSummary,
  Showdown,
  SpectatorPanel,
  Table,
};
